import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, ActivityIndicator, Modal, Image, Dimensions } from 'react-native';
import { Upload, X, File, Image as ImageIcon, Download, Trash2, Eye } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { downloadFile } from '../lib/fileDownload';
import * as DocumentPicker from 'expo-document-picker';

interface Attachment {
  id: string;
  file_url: string;
  file_name: string;
  file_size: number;
  file_type: string;
  uploaded_by: string;
  created_at: string;
}

interface ServiceRequestFileUploadProps {
  serviceRequestId: string | null;
  onAttachmentsChange?: (attachments: Attachment[]) => void;
  disabled?: boolean;
}

const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB in bytes

export const ServiceRequestFileUpload: React.FC<ServiceRequestFileUploadProps> = ({
  serviceRequestId,
  onAttachmentsChange,
  disabled = false
}) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  useEffect(() => {
    if (serviceRequestId) {
      fetchAttachments();
    }
  }, [serviceRequestId]);

  const fetchAttachments = async () => {
    if (!serviceRequestId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('service_request_attachments')
        .select('*')
        .eq('service_request_id', serviceRequestId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setAttachments(data || []);
      onAttachmentsChange?.(data || []);
    } catch (err: any) {
      console.error('Error fetching attachments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const validateFile = (fileSize: number, fileName: string): string | null => {
    if (fileSize > MAX_FILE_SIZE) {
      return `File "${fileName}" is too large. Maximum size is 200MB.`;
    }
    return null;
  };

  const uploadFile = async (fileUri: string, fileName: string, mimeType: string, fileSize: number) => {
    if (!serviceRequestId) {
      setError('Service request ID is required');
      return;
    }

    const validationError = validateFile(fileSize, fileName);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Fetch the file as blob
      const response = await fetch(fileUri);
      const blob = await response.blob();

      // Create a unique file path
      const timestamp = Date.now();
      const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${serviceRequestId}/${timestamp}_${sanitizedFileName}`;

      // Upload file to storage
      const { error: uploadError } = await supabase.storage
        .from('service-request-attachments')
        .upload(filePath, blob, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Insert metadata into database
      const { error: dbError } = await supabase
        .from('service_request_attachments')
        .insert({
          service_request_id: serviceRequestId,
          file_url: filePath,
          file_name: fileName,
          file_size: fileSize,
          file_type: mimeType || 'application/octet-stream',
          uploaded_by: user.id
        });

      if (dbError) {
        // If database insert fails, delete the uploaded file
        await supabase.storage
          .from('service-request-attachments')
          .remove([filePath]);
        throw dbError;
      }

      setUploadProgress(100);
      await fetchAttachments();
    } catch (err: any) {
      console.error('Error uploading file:', err);
      setError(err.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFilePick = async () => {
    if (disabled || uploading) return;

    try {
      if (Platform.OS === 'web') {
        // On web, create a file input element
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.onchange = async (e: any) => {
          const files = Array.from(e.target.files || []) as File[];
          for (const file of files) {
            // Convert File to blob URL for upload
            const blob = new Blob([file], { type: file.type });
            const blobUrl = URL.createObjectURL(blob);
            await uploadFile(blobUrl, file.name, file.type, file.size);
            URL.revokeObjectURL(blobUrl);
          }
        };
        input.click();
      } else {
        // On native, use expo-document-picker
        const result = await DocumentPicker.getDocumentAsync({
          type: '*/*',
          multiple: true,
          copyToCacheDirectory: true,
        });

        if (result.canceled) return;

        for (const doc of result.assets) {
          await uploadFile(
            doc.uri,
            doc.name || 'file',
            doc.mimeType || 'application/octet-stream',
            doc.size || 0
          );
        }
      }
    } catch (error: any) {
      console.error('Error picking files:', error);
      setError(error.message || 'Failed to pick files');
    }
  };

  const deleteAttachment = async (attachment: Attachment) => {
    if (!confirm(`Are you sure you want to delete "${attachment.file_name}"?`)) {
      return;
    }

    try {
      // Delete from storage first
      const { error: storageError } = await supabase.storage
        .from('service-request-attachments')
        .remove([attachment.file_url]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('service_request_attachments')
        .delete()
        .eq('id', attachment.id);

      if (dbError) throw dbError;

      await fetchAttachments();
    } catch (err: any) {
      console.error('Error deleting attachment:', err);
      setError(err.message);
    }
  };

  const downloadAttachment = async (attachment: Attachment) => {
    const { data: { publicUrl } } = supabase.storage
      .from('service-request-attachments')
      .getPublicUrl(attachment.file_url);

    await downloadFile(publicUrl, attachment.file_name);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <ImageIcon size={20} color="#6b7280" />;
    if (fileType.startsWith('video/')) return <File size={20} color="#6b7280" />;
    if (fileType === 'application/pdf') return <File size={20} color="#6b7280" />;
    return <File size={20} color="#6b7280" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImageFile = (fileType: string | null | undefined, fileName?: string) => {
    // Always check file extension first as it's more reliable
    if (fileName) {
      const extension = fileName.toLowerCase().split('.').pop();
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'heic', 'heif'];
      if (imageExtensions.includes(extension || '')) return true;
    }
    
    // Check MIME type as fallback
    if (fileType && fileType.startsWith('image/')) return true;
    
    return false;
  };

  const viewImage = (attachment: Attachment) => {
    const { data: { publicUrl } } = supabase.storage
      .from('service-request-attachments')
      .getPublicUrl(attachment.file_url);
    setPreviewImage(publicUrl);
  };

  return (
    <View style={styles.container}>
      {/* Upload Area */}
      {!disabled && serviceRequestId && (
        <Pressable
          onPress={handleFilePick}
          disabled={uploading}
          style={[styles.uploadArea, uploading && styles.uploadAreaDisabled]}
        >
          {uploading ? (
            <View style={styles.uploadingContent}>
              <ActivityIndicator size="large" color="#2563eb" />
              <Text style={styles.uploadingText}>Uploading... {uploadProgress}%</Text>
            </View>
          ) : (
            <>
              <Upload size={32} color="#9ca3af" />
              <Text style={styles.uploadText}>
                {Platform.OS === 'web' ? 'Drag and drop files here, or click to select' : 'Tap to select files'}
              </Text>
              <Text style={styles.uploadSubtext}>
                Maximum file size: 200MB
              </Text>
            </>
          )}
        </Pressable>
      )}

      {error && (
        <View style={styles.errorContainer}>
          <X size={16} color="#dc2626" />
          <Text style={styles.errorText}>{error}</Text>
          <Pressable
            onPress={() => setError(null)}
            style={styles.errorCloseButton}
          >
            <X size={16} color="#dc2626" />
          </Pressable>
        </View>
      )}

      {/* Attachments List */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#9ca3af" />
        </View>
      ) : attachments.length > 0 ? (
        <View style={styles.attachmentsContainer}>
          <Text style={styles.attachmentsTitle}>
            Attachments ({attachments.length})
          </Text>
          <View style={styles.attachmentsList}>
            {attachments.map((attachment) => (
              <View key={attachment.id} style={styles.attachmentItem}>
                <View style={styles.attachmentContent}>
                  <View style={styles.attachmentIcon}>
                    {getFileIcon(attachment.file_type)}
                  </View>
                  <View style={styles.attachmentInfo}>
                    <Text style={styles.attachmentName} numberOfLines={1}>
                      {attachment.file_name}
                    </Text>
                    <Text style={styles.attachmentMeta}>
                      {formatFileSize(attachment.file_size)} • {new Date(attachment.created_at).toLocaleDateString()}
                    </Text>
                  </View>
                </View>
                <View style={styles.attachmentActions}>
                  {isImageFile(attachment.file_type, attachment.file_name) && (
                    <Pressable
                      onPress={() => viewImage(attachment)}
                      style={[styles.actionButton, styles.viewButton]}
                      title="View image"
                    >
                      <Eye size={20} color="#ffffff" />
                    </Pressable>
                  )}
                  <Pressable
                    onPress={() => downloadAttachment(attachment)}
                    style={[styles.actionButton, styles.downloadButton]}
                    title="Download file"
                  >
                    <Download size={20} color="#ffffff" />
                  </Pressable>
                  {!disabled && (
                    <Pressable
                      onPress={() => deleteAttachment(attachment)}
                      style={styles.actionButton}
                      title="Delete file"
                    >
                      <Trash2 size={18} color="#dc2626" />
                    </Pressable>
                  )}
                </View>
              </View>
            ))}
          </View>
        </View>
      ) : serviceRequestId ? (
        <Text style={styles.noAttachmentsText}>
          No attachments yet
        </Text>
      ) : null}

      {/* Image Preview Modal */}
      <Modal
        visible={!!previewImage}
        transparent
        animationType="fade"
        onRequestClose={() => setPreviewImage(null)}
      >
        <View style={styles.modalOverlay}>
          <Pressable
            style={styles.modalOverlayBackground}
            onPress={() => setPreviewImage(null)}
          />
          <View style={styles.modalContent}>
            <Pressable
              onPress={() => setPreviewImage(null)}
              style={styles.modalCloseButton}
            >
              <X size={24} color="#ffffff" />
            </Pressable>
            {previewImage && (
              <Image
                source={{ uri: previewImage }}
                style={styles.previewImage}
                resizeMode="contain"
              />
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  uploadArea: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: '#d1d5db',
    borderRadius: 8,
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  uploadAreaDisabled: {
    opacity: 0.5,
  },
  uploadingContent: {
    alignItems: 'center',
    gap: 8,
  },
  uploadingText: {
    fontSize: 14,
    color: '#4b5563',
  },
  uploadText: {
    fontSize: 14,
    color: '#4b5563',
    marginTop: 8,
    textAlign: 'center',
  },
  uploadSubtext: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fef2f2',
    padding: 12,
    borderRadius: 8,
    gap: 8,
  },
  errorText: {
    flex: 1,
    fontSize: 14,
    color: '#dc2626',
  },
  errorCloseButton: {
    padding: 4,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
  },
  attachmentsContainer: {
    gap: 8,
  },
  attachmentsTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  attachmentsList: {
    gap: 8,
  },
  attachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    gap: 12,
  },
  attachmentContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
    minWidth: 0,
  },
  attachmentIcon: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  attachmentInfo: {
    flex: 1,
    minWidth: 0,
  },
  attachmentName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  attachmentMeta: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },
  attachmentActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionButton: {
    padding: 8,
    borderRadius: 6,
    minWidth: 40,
    minHeight: 40,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  viewButton: {
    backgroundColor: '#2563eb',
    borderColor: '#1e40af',
  },
  downloadButton: {
    backgroundColor: '#16a34a',
    borderColor: '#15803d',
  },
  noAttachmentsText: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    paddingVertical: 16,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    position: 'relative',
  },
  modalOverlayBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
  },
  modalContent: {
    width: '100%',
    maxWidth: 896,
    height: Dimensions.get('window').height * 0.9,
    maxHeight: Dimensions.get('window').height * 0.9,
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  modalCloseButton: {
    position: 'absolute',
    top: -40,
    right: 0,
    zIndex: 2,
    padding: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 4,
  },
  previewImage: {
    width: '100%',
    height: '100%',
    borderRadius: 8,
  },
});

export default ServiceRequestFileUpload;
